<template>
  <div id="cropper" class="">
    <cropper
      ref="cropperRef"
      class="cropper"
      :src="image"
      :default-size="defaultSize"
      :stencil-props="{
        aspectRatio: 1 / 1
      }"
    />
    <div class="flex items-center justify-between m-4 gap-2">
      <div class="flex-shrink-0 flex justify-start items-center flex-1 gap-2">
        <div
          @click="zoomOut()"
          class="flex justify-center items-center rounded w-9 h-9 cursor-pointer bg-gradient-to-l from-linear-s to-linear-e hover:opacity-75"
        >
          <zoomOutSvg />
        </div>
        <div
          @click="zoomIn()"
          class="flex justify-center items-center rounded w-9 h-9 cursor-pointer bg-gradient-to-l from-linear-s to-linear-e hover:opacity-75"
        >
          <zoomInSvg />
        </div>
        <div
          @click="flip(true, false)"
          class="flex justify-center items-center rounded w-9 h-9 cursor-pointer bg-gradient-to-l from-linear-s to-linear-e hover:opacity-75"
        >
          <flipHorizontal />
        </div>
        <div
          @click="flip(false, true)"
          class="flex justify-center items-center rounded w-9 h-9 cursor-pointer bg-gradient-to-l from-linear-s to-linear-e hover:opacity-75"
        >
          <flipVertical />
        </div>
        <div
          @click="rotate(45)"
          class="flex justify-center items-center rounded w-9 h-9 cursor-pointer bg-gradient-to-l from-linear-s to-linear-e hover:opacity-75"
        >
          <rotateClockwise />
        </div>
        <div
          @click="rotate(-45)"
          class="flex justify-center items-center rounded w-9 h-9 cursor-pointer bg-gradient-to-l from-linear-s to-linear-e hover:opacity-75"
        >
          <rotateCounterClockwise />
        </div>
      </div>

      <button
        class="border-2 bg-brand-gray-600 text-white rounded min-w-[100px] p-2 hover:opacity-80"
        @click="cancel()"
      >
        Cancel
      </button>
      <button
        class="border-2 border-primary bg-gradient-to-l from-linear-s to-linear-e text-white rounded min-w-[100px] p-2 hover:opacity-80"
        @click="cropImage()"
      >
        {{ $t('GetCroppedCanvas') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onUnmounted, defineEmits } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import flipHorizontal from '@/assets/svgs/flip-horizontal.svg?component'
import flipVertical from '@/assets/svgs/flip-vertical.svg?component'
import rotateClockwise from '@/assets/svgs/rotate-clockwise.svg?component'
import rotateCounterClockwise from '@/assets/svgs/rotate-counter-clockwise.svg?component'
import zoomInSvg from '@/assets/svgs/zoom-in.svg?component'
import zoomOutSvg from '@/assets/svgs/zoom-out.svg?component'
import download from '@/assets/svgs/download.svg?component'

const props = defineProps({
  defaultSize: {
    type: Object,
    default: () => ({ width: 400, height: 400 })
  },
  image: {
    type: Object,
    required: true
  }
})

const emits = defineEmits(['cancel', 'confirm'])

const cropperRef = ref()

const cancel = () => {
  emits('cancel')
}

const cropImage = () => {
  if (cropperRef.value) {
    const { canvas } = cropperRef.value.getResult()
    if (canvas) {
      canvas.toBlob((blob) => emits('confirm', blob))
    }
  }
}

const zoomIn = () => {
  cropperRef.value.zoom(2)
}

const zoomOut = () => {
  cropperRef.value.zoom(0.5)
}

const flip = (x, y) => {
  const { image } = cropperRef.value.getResult()
  if (image.transforms.rotate % 180 !== 0) {
    cropperRef.value.flip(!x, !y)
  } else {
    cropperRef.value.flip(x, y)
  }
}

const rotate = (angle) => {
  cropperRef.value.rotate(angle)
}

onUnmounted(() => {
  if (props.image) {
    URL.revokeObjectURL(props.image)
  }
})
</script>

<style lang="scss">
.cropper {
  max-height: 500px;
  transition-duration: 0;
}

.button {
  color: white;
  font-size: 16px;
  padding: 10px 20px;
  width: 100%;
  background: #151515;
  cursor: pointer;
  transition: background 0.5s;
  border: none;
  &:not(:last-of-type) {
    margin-right: 10px;
  }
  &:hover {
    background: #2f2f2f;
  }
  input {
    display: none;
  }
}
</style>
